import React, { useState } from "react";
import Button from "../Common/Button";
import * as yup from "yup";
import { Formik } from "formik";
import { InputGroup } from "react-bootstrap";
import Form from "react-bootstrap/Form";
import { toast } from "react-toastify";
import LazyLoad from "react-lazyload";
import OtpComponent from "./OtpComponent"; // Import OtpComponent
import { sendOtp, resetPassword } from "../../utils/authService";

const emailSchema = yup.object({
  email: yup
    .string()
    .email("Invalid email address")
    .required("Email is required"),
});

const newPasswordSchema = yup.object({
  newPassword: yup
    .string()
    .required("New password is required")
    .min(8, "Password must be at least 8 characters"),
  confirmPassword: yup
    .string()
    .required("Confirm password is required")
    .oneOf([yup.ref("newPassword"), null], "Passwords must match"),
});

const PasswordReset = ({ setForgotPassword }) => {
  const [forgotPasswordStep, setForgotPasswordStep] = useState("email");
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");

  const handleEmailSubmit = async (values) => {
    setLoading(true);
    try {
      setEmail(values.email);
      const response = await sendOtp(JSON.stringify({ email: values.email }));
      if (response.ok) {
        setForgotPasswordStep("otp");
      } else {
        toast.error("Failed to send OTP. Please try again.");
      }
    } catch (error) {
      toast.error("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleOtpSuccess = (enteredOtp) => {
    setOtp(enteredOtp); // Save OTP to be used when resetting the password
    setForgotPasswordStep("newPassword");
  };

  const handleNewPasswordSubmit = async (values) => {
    setLoading(true);
    try {
      const response = await resetPassword(
        JSON.stringify({
          email,
          otp,
          newPassword: values.newPassword,
        })
      );
      if (response.ok) {
        setForgotPasswordStep("passwordReset");
        toast.success("Password reset successful!");
      } else {
        toast.error("Password reset failed. Please try again.");
      }
    } catch (error) {
      toast.error("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    if (forgotPasswordStep === "email") {
      await handleEmailSubmit(values);
    } else if (forgotPasswordStep === "newPassword") {
      await handleNewPasswordSubmit(values);
    }
  };

  return (
    <Formik
      initialValues={
        forgotPasswordStep === "email"
          ? { email: "" }
          : { newPassword: "", confirmPassword: "" }
      }
      validationSchema={
        forgotPasswordStep === "email" ? emailSchema : newPasswordSchema
      }
      onSubmit={handleSubmit}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleSubmit,
        handleBlur,
      }) => (
        <div className="right-side">
          {forgotPasswordStep !== "passwordReset" && (
            <div className="login-heading">
              <h3>Forgot Password</h3>
            </div>
          )}

          {/* Step 1: Email Input */}
          {forgotPasswordStep === "email" && (
            <div className="mobile-forgot">
              <div className="forgot-icon">
                <LazyLoad height={20} offset={100}>
                  <img src="/forgotPassword.svg" alt="forgotPasswordIcon" />
                </LazyLoad>
              </div>
              <h4>Forgot your password?</h4>
              <label>
                Enter your registered email below to receive password reset
                instructions
              </label>
            </div>
          )}

          {forgotPasswordStep === "email" && (
            <div className="w-100">
              <label htmlFor="email">Email Address</label>
              <Form.Control
                name="email"
                type="email"
                placeholder="Enter your email"
                value={values.email}
                onChange={handleChange}
                onBlur={handleBlur}
              />
              <p className="text-danger">
                {errors.email && touched.email && errors.email}
              </p>
            </div>
          )}

          {/* Step 2: OTP Input */}
          {forgotPasswordStep === "otp" && (
            <OtpComponent
              email={email}
              purpose="passwordReset"
              onSuccess={handleOtpSuccess}
            />
          )}

          {/* Step 3: New Password Input */}
          {forgotPasswordStep === "newPassword" && (
            <React.Fragment>
              <div className="w-100">
                <Form.Group controlId="newPassword">
                  <Form.Label>Add new password</Form.Label>
                  <InputGroup>
                    <Form.Control
                      name="newPassword"
                      type="password"
                      placeholder="Enter your new password"
                      value={values.newPassword}
                      onBlur={handleBlur}
                      onChange={handleChange}
                    />
                  </InputGroup>
                </Form.Group>
                <p className="text-danger">
                  {errors.newPassword &&
                    touched.newPassword &&
                    errors.newPassword}
                </p>
              </div>
              <div className="w-100">
                <Form.Group controlId="confirmPassword">
                  <Form.Label>Re-enter password</Form.Label>
                  <InputGroup>
                    <Form.Control
                      name="confirmPassword"
                      type="password"
                      placeholder="Confirm your new password"
                      value={values.confirmPassword}
                      onBlur={handleBlur}
                      onChange={handleChange}
                    />
                  </InputGroup>
                </Form.Group>
                <p className="text-danger">
                  {errors.confirmPassword &&
                    touched.confirmPassword &&
                    errors.confirmPassword}
                </p>
              </div>
            </React.Fragment>
          )}

          {/* Reset Password Button */}
          {forgotPasswordStep !== "otp" &&
            forgotPasswordStep !== "passwordReset" && (
              <Button
                disabled={loading}
                className="w-100 mt-3"
                type="submit"
                onClick={handleSubmit}
              >
                {loading ? "Submitting..." : "Send"}
              </Button>
            )}

          {/* Go back to login */}
          {forgotPasswordStep !== "otp" &&
            forgotPasswordStep !== "passwordReset" && (
              <div className="d-flex mt-4">
                <p style={{ margin: "0" }}>You remember your password?</p>
                <p
                  className="signup-link mx-1"
                  onClick={() => setForgotPassword(false)}
                >
                  Login
                </p>
              </div>
            )}

          {/* Password Reset Success Message */}
          {forgotPasswordStep === "passwordReset" && (
            <div className="d-flex justify-content-center flex-column text-center text-md-start">
              <h3>Your Password has been changed successfully</h3>
              <label className="w-100 mt-3 mt-md-1">
                Login with the new password to continue{" "}
                <Button
                  className="w-100 mt-md-4 mt-5"
                  onClick={() => setForgotPassword(false)}
                >
                  Login
                </Button>
              </label>
            </div>
          )}
        </div>
      )}
    </Formik>
  );
};

export default PasswordReset;
